import React, { useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import { injectIntl, useIntl } from "react-intl";
import { compose } from "redux";
import { useDispatch, useSelector } from "react-redux";
import { Col, Row } from "@domestika-dev/prudencio/dist/atoms/Layout";
import * as ReducerKeys from "../constants/reducerKeys";
import * as LiveQueryParams from "../constants/liveQueryParams";
import * as LiveSessionStates from "../constants/liveSessionStates";
import LoaderModal from "../../../common/LoaderModal";
import {
  LiveSessionAlert,
  LiveSetupModal,
  NotificationsAlert,
  LiveSessionsListNextLive,
  LiveSessionsListPreviousLives,
  LiveSessionsListScheduleNew,
  LiveSessionsListSetupInstructions,
  LiveSessionConfirmationModal,
  LiveSessionsListFAQ,
} from "../component";
import injectMultipleReducers from "../../../utils/reducers/injectMultipleReducers";
import injectSaga from "../../../utils/saga/injectSaga";
import {
  courseReducer,
  liveChannelReducer,
  liveSessionReducer,
  liveSessionsListPageReducer,
  liveSessionsListReducer,
  userReducer,
} from "../reducer";
import {
  redirectToLivePage,
  redirectToLiveRecordingPage,
  redirectToScheduleEditLive,
  redirectToScheduleNewLive,
} from "../utils/pageRedirect";
import { isUserCourseOnwer } from "../utils/user";
import saga from "../saga";
import {
  useCourseActions,
  useLiveChannelActions,
  useLiveSessionActions,
  useLiveSessionsListPageActions,
  useUserDomainActions,
  useLiveSessionDomainActions,
} from "../customHooks";
import * as LiveSetupModalModes from "../constants/liveSetupModalModes";
import * as LiveSetupSteps from "../constants/liveSetupSteps";
import * as UserRoles from "../constants/roles";
import { LiveSessionProvider } from "../context/LiveSessionContext";
import When from "../../../common/When";
import {
  setupHoneybadger,
  removeHoneybadger,
} from "../../../../js/utils/errorTracking/honeybadger";

const removeQueryParam = () => {
  window.history.replaceState({}, document.title, window.location.pathname);
};

export const LiveSessionsList = ({ courseId }) => {
  const intl = useIntl();

  const {
    loading: courseLoading,
    errorFetching: courseFetchingError,
    course,
  } = useSelector((state) => state[ReducerKeys.COURSE]);

  const {
    loading: liveChannelLoading,
    errorFetching: liveChannelFetchingError,
    liveChannel,
  } = useSelector((state) => state[ReducerKeys.LIVE_CHANNEL]);

  const {
    loading: liveSessionLoading,
    errorFetching: liveSessionFetchingError,
    liveSession,
    liveSessionFetchSuccess,
    cancelledStateSuccess,
  } = useSelector((state) => state[ReducerKeys.LIVE_SESSION]);

  const {
    loading: liveSessionsListLoading,
    errorFetching: liveSessionsListFetchingError,
    liveSessionsList,
    liveSessionsListNextPage,
    isLastLiveSessionsListPage,
  } = useSelector((state) => state[ReducerKeys.LIVE_SESSIONS_LIST]);

  const { loading: liveSessionsListPageLoading } = useSelector(
    (state) => state[ReducerKeys.LIVE_SESSIONS_LIST_PAGE]
  );

  const { loadingUser, errorFetchingUser, user } = useSelector((state) => state[ReducerKeys.USER]);

  const isTeacher = () => isUserCourseOnwer(course, user);

  const dispatch = useDispatch();
  const { getCourse } = useCourseActions();
  const { getCurrentWithoutRelationshipsUser } = useUserDomainActions({ dispatch });
  const { getLiveChannel } = useLiveChannelActions();
  const { cancelLiveSession } = useLiveSessionActions({ dispatch });
  const { getLiveSessionsList, getActiveLiveSessionByCourse } = useLiveSessionDomainActions();
  const { setLiveSessionsListPageLoading } = useLiveSessionsListPageActions({ dispatch });

  const [hasActiveSession, setHasActiveSession] = useState(false);
  const [isLiveSetupModalOpen, setLiveSetupModalOpen] = useState(false);
  const [liveSetupModalStep, setLiveSetupModalStep] = useState();
  const [isConfirmationModalOpen, setConfirmationModalOpen] = useState(false);
  const showLiveSetupModalParam = useRef(null);

  const showLiveSessionAlert = () =>
    courseFetchingError ||
    liveChannelFetchingError ||
    liveSessionFetchingError ||
    liveSessionsListFetchingError ||
    errorFetchingUser;

  const goToScheduleNewPage = () => {
    setLiveSessionsListPageLoading();
    redirectToScheduleNewLive(courseId);
  };

  const goToScheduleEditPage = () => {
    if (liveSession?.id) {
      setLiveSessionsListPageLoading();
      redirectToScheduleEditLive(courseId, liveSession?.id);
    }
  };

  const goToLivePage = () => {
    if (liveSession?.id) {
      setLiveSessionsListPageLoading();
      redirectToLivePage(courseId, liveSession?.id);
    }
  };

  const goToLiveRecordingPage = (liveSessionId) => {
    if (liveSessionId) {
      setLiveSessionsListPageLoading();
      redirectToLiveRecordingPage(courseId, liveSessionId);
    }
  };

  const openLiveSetupModal = (liveSetupStep) => {
    setLiveSetupModalStep(liveSetupStep);

    if (
      liveSetupStep === LiveSetupSteps.CREDENTIALS_SETUP &&
      !liveChannel?.id &&
      !liveChannelLoading
    ) {
      getLiveChannel(course.relationships?.liveChannel?.data?.id);
      return;
    }

    setLiveSetupModalOpen(true);
  };

  const handleLiveSetupModalClose = () => {
    setLiveSetupModalOpen(false);
    if (showLiveSetupModalParam) {
      removeQueryParam();
    }
  };

  const openConfirmationModal = () => {
    setConfirmationModalOpen(true);
  };

  const closeConfirmationModal = () => {
    setConfirmationModalOpen(false);
  };

  const onConfirmModalHandler = () => {
    closeConfirmationModal();
    cancelLiveSession(liveSession?.id);
  };

  const fetchNextLiveSessionsListPage = () => {
    getLiveSessionsList({
      sort: [{ field: "startedAt", order: "desc" }],
      pageNumber: liveSessionsListNextPage,
      pageSize: 12,
      courseId,
      state: [LiveSessionStates.FINISHED],
      hasRecording: true,
    });
  };

  const getUserRole = () => (isTeacher() ? UserRoles.TEACHER : UserRoles.STUDENT);

  const ContainerContent = {
    NotificationsAlert: () => <NotificationsAlert />,
    ScheduleNew: () => <LiveSessionsListScheduleNew />,
    NextLive: () => <LiveSessionsListNextLive />,
    PreviousLives: () => <LiveSessionsListPreviousLives />,
    SetupInstructions: () => (
      <LiveSessionsListSetupInstructions
        liveChannelLoading={liveChannelLoading}
        onFirstInstructionClick={() => openLiveSetupModal(LiveSetupSteps.CREDENTIALS_SETUP)}
        onSecondInstructionClick={() => openLiveSetupModal(LiveSetupSteps.CREATE_LOBBY)}
      />
    ),
    FAQ: () => <LiveSessionsListFAQ />,
  };

  const RoleContainerChildren = {
    [UserRoles.STUDENT]: [
      { show: !!user.id && !!course.id, content: ContainerContent.NotificationsAlert },
      { show: true, content: ContainerContent.NextLive },
      {
        show: liveSessionsListLoading || liveSessionsList?.length,
        content: ContainerContent.PreviousLives,
      },
    ],
    [UserRoles.TEACHER]: [
      { show: true, content: ContainerContent.ScheduleNew },
      { show: liveSessionLoading || hasActiveSession, content: ContainerContent.NextLive },
      { show: true, content: ContainerContent.PreviousLives },
      { show: true, content: ContainerContent.SetupInstructions },
      { show: true, content: ContainerContent.FAQ },
    ],
  };

  const getContainerContent = () =>
    RoleContainerChildren[getUserRole()]
      .filter(({ show }) => !!show)
      .map(({ content }, index) => (
        <Row key={`live-sessions-list-section-${index + 1}`}>{content()}</Row>
      ));

  useEffect(() => {
    getCourse(courseId);
    getCurrentWithoutRelationshipsUser();
    getActiveLiveSessionByCourse({ courseId });
    fetchNextLiveSessionsListPage();
    setupHoneybadger();

    const queryParams = new URLSearchParams(window.location.search);
    showLiveSetupModalParam.current =
      queryParams.get(LiveQueryParams.SHOW_SETUP_INSTRUCTIONS) === "true";

    return () => {
      removeHoneybadger();
    };
  }, []);

  useEffect(() => {
    if (course?.id && user?.id) {
      if (showLiveSetupModalParam.current === true) {
        if (isTeacher()) {
          openLiveSetupModal(LiveSetupSteps.CREDENTIALS_SETUP);
        } else {
          removeQueryParam();
        }
      }
    }
  }, [course, user]);

  useEffect(() => {
    if (liveSessionFetchSuccess) {
      setHasActiveSession(!!liveSession?.id);
    }
  }, [liveSessionFetchSuccess, liveSession]);

  useEffect(() => {
    if (cancelledStateSuccess) {
      setHasActiveSession(!!liveSession?.id);
    }
  }, [cancelledStateSuccess]);

  useEffect(() => {
    if (!liveChannelLoading && liveChannel?.id) {
      setLiveSetupModalOpen(true);
    }
  }, [liveChannelLoading, liveChannel]);

  return (
    <>
      <LoaderModal
        isOpen={liveSessionsListPageLoading}
        messageKey="live_sessions.loading_message"
      />
      <When condition={hasActiveSession && isTeacher()}>
        {() => (
          <LiveSessionConfirmationModal
            isOpen={isConfirmationModalOpen}
            title={intl.formatMessage({ id: "live_sessions.teacher_delete_live.title" })}
            body={intl.formatMessage({ id: "live_sessions.teacher_delete_live.body" })}
            cancelMessage={intl.formatMessage({ id: "generic.cancel" })}
            confirmMessage={intl.formatMessage({
              id: "live_sessions.teacher_delete_live.button",
            })}
            onConfirm={onConfirmModalHandler}
            onCancel={closeConfirmationModal}
          />
        )}
      </When>
      <When condition={showLiveSessionAlert()}>
        {() => <LiveSessionAlert type="error" message="generic.messages.error_try_again" top={8} />}
      </When>
      <When condition={isLiveSetupModalOpen}>
        {() => (
          <LiveSetupModal
            liveChannel={liveChannel}
            onClose={handleLiveSetupModalClose}
            initialStep={liveSetupModalStep}
            mode={LiveSetupModalModes.READ_ONLY}
            enableNavigation={false}
          />
        )}
      </When>
      <Row>
        <Col width={[1, 3 / 4, 3 / 4]}>
          <LiveSessionProvider
            value={{
              hasActiveSession,
              liveSession,
              course,
              user,
              isTeacher: isTeacher(),
              liveSessionsList,
              liveSessionsListNextPage,
              isLastLiveSessionsListPage,
              courseLoading,
              userLoading: loadingUser,
              nextLiveLoading: liveSessionLoading,
              previousLivesLoading: liveSessionsListLoading,
              onEnterLive: goToLivePage,
              onScheduleCancel: openConfirmationModal,
              onScheduleEdit: goToScheduleEditPage,
              onScheduleNew: goToScheduleNewPage,
              onRequestNextPage: fetchNextLiveSessionsListPage,
              onEnterLiveRecording: goToLiveRecordingPage,
            }}
          >
            {getContainerContent()}
          </LiveSessionProvider>
        </Col>
      </Row>
    </>
  );
};

const withReducers = injectMultipleReducers([
  { key: ReducerKeys.COURSE, reducer: courseReducer },
  { key: ReducerKeys.LIVE_CHANNEL, reducer: liveChannelReducer },
  { key: ReducerKeys.LIVE_SESSION, reducer: liveSessionReducer },
  { key: ReducerKeys.LIVE_SESSIONS_LIST, reducer: liveSessionsListReducer },
  { key: ReducerKeys.LIVE_SESSIONS_LIST_PAGE, reducer: liveSessionsListPageReducer },
  { key: ReducerKeys.USER, reducer: userReducer },
]);

const withSaga = injectSaga({ key: "course[live]", saga });

export default compose(withReducers, withSaga)(injectIntl(LiveSessionsList));

LiveSessionsList.propTypes = {
  courseId: PropTypes.number.isRequired,
};
